import RightArrowIcon from "@/assets/colorable/right_arrow.svg?react";
import useContentIcons from "@/hooks/useContentIcons";
import clsxm from "@/lib/clsxm";
import {useCarouselStore, type TCarouselMedia} from "@/store/carousel";
import {getContentPermalink, getContentPublishedDate} from "@/utils/mediaUtils";
import {Link} from "@tanstack/react-router";
import dayjs from "dayjs";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

const CarouselHeader: FC<{
  currentMediaIndex: number;
  mediaLength: number;
  currentMedia: TCarouselMedia;
}> = ({currentMediaIndex, currentMedia, mediaLength}) => {
  const {t} = useTranslation("common");
  const content = useContentIcons(currentMedia.instagram_media?.media_product_type!);
  const carouselCampaign = useCarouselStore(state => state.campaign);
  const campaign = currentMedia.campaign ?? carouselCampaign;
  const variant = useCarouselStore(state => state.variant);
  const permalink = getContentPermalink({media: currentMedia.instagram_media});
  // Source material and UGC only ever carry "created" -- and the statistics bar, which would
  // otherwise show their date, renders nothing for those two types.
  const publishedAt = getContentPublishedDate({media: currentMedia.instagram_media});

  const renderUploadedDate = () =>
    !currentMedia.caption &&
    !!publishedAt && (
      <span className="shrink-0 text-sm whitespace-nowrap text-white">
        <span className="mx-1 text-base">•</span>
        {t("uploaded")} {dayjs(publishedAt).format("DD.MM.YYYY")}
      </span>
    );

  if (variant === "assets" && !!currentMedia.assetName) {
    return (
      <div className="ml-3 flex items-center text-base font-normal text-white">
        <span className="inline-block max-w-[calc(100%-64px)] truncate">{currentMedia.assetName}&nbsp;</span>
        <span>{" • " + (currentMediaIndex + 1) + "/" + mediaLength}</span>
      </div>
    );
  }

  if (variant === "social_feed") {
    return (
      <div className="flex items-center">
        <img src={currentMedia.user?.picture!} className="size-7 shrink-0 rounded-full" />
        <Link
          to="/influencer/$influencerId"
          params={{influencerId: currentMedia.user?.influencer_id!}}
          target="_blank"
          className="ml-3 text-left text-base font-normal text-white no-underline hover:text-white/90">
          {currentMedia.user?.first_name}
        </Link>{" "}
        {renderUploadedDate()}
      </div>
    );
  }

  return (
    <div className="flex min-w-0 items-center overflow-hidden pr-8">
      {!!currentMedia.user?.picture && <img src={currentMedia.user.picture} className="size-7 shrink-0 rounded-full" />}
      <div className={clsxm("flex min-w-0 items-center gap-1 text-white", !!currentMedia.user?.picture && "ml-3")}>
        <span className="min-w-0 truncate text-left text-base font-normal text-white">
          {!!currentMedia.user?.first_name && (
            <>
              <Link
                to="/influencer/$influencerId"
                params={{influencerId: currentMedia.user?.influencer_id!}}
                target="_blank"
                className="text-white no-underline hover:text-white/90">
                {currentMedia.user.first_name}
              </Link>{" "}
              •{" "}
            </>
          )}
          {!!campaign && (
            <Link
              to="/campaign/$campaignId"
              params={{campaignId: campaign.id}}
              target="_blank"
              className="text-white no-underline hover:text-white/90">
              {campaign.title}
            </Link>
          )}
        </span>
        <span className="shrink-0 text-base font-normal text-white">
          {currentMediaIndex + 1}/{mediaLength}
        </span>
        {renderUploadedDate()}
        <span className="hidden sm:inline">•</span>
        <div
          className={clsxm(
            "hidden items-center gap-1.5 sm:flex",
            !!permalink &&
              "h-8 cursor-pointer rounded-md border border-white/15 bg-white/10 px-3 text-sm font-medium transition-colors hover:bg-white/15",
          )}
          onClick={() => {
            if (permalink) window.open(permalink, "_blank");
          }}>
          <content.Icon className="size-4 shrink-0" />
          <span className="text-sm">{t(content.translationKey as "story")}</span>
          {!!permalink && <RightArrowIcon className="size-3 shrink-0" />}
        </div>
      </div>
    </div>
  );
};

export default CarouselHeader;
